/**
 * This component (WalletOnboarding not WalletOnboardingModal) is should be deprecated soon and will be removed in a future version.
 * Use UnifiedActivationFlow instead, which provides a metadata-driven approach
 * for both pre-account (sign-up) and post-account (activation) flows.
 *
 * Migration guide:
 * - Replace WalletOnboarding with UnifiedActivationFlow
 */

import { ArrowLeftIcon } from '@heroicons/react/outline';
import accountableWalletGraphic from 'assets/images/accountable-bank-card.png';
import walletBannerGraphics from 'assets/images/accountable-wallet-banner-graphic.png';

import { Button } from 'components/atoms/Button';
import GraphicButton from 'components/atoms/GraphicButton';
import { Modal, type ModalApi } from 'components/atoms/Modal';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import UnifiedBankingSignupFlow from './UnifiedBankingSignupFlow';
import EuroCircleIcon from 'components/atoms/SVG/EuroCircleIcon.component';
import FilePercentIcon from 'components/atoms/SVG/FilePercentIcon.component';
import GivingCoinsIcon from 'components/atoms/SVG/GivingCoinsIcon.component';
import PegIconOutline from 'components/atoms/SVG/PegIconOutline.component';
import WalletPercentIcon from 'components/atoms/SVG/WalletPercentIcon.component';
import UseEffect from 'components/atoms/UseEffect';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCreateBankConnection from 'hooks/Bank/useCreateBankConnection';
import useGetBankingOnboardingStatus from 'hooks/Bank/useGetBankingOnboardingStatus';
import useInitiateAccountableBankingOnboarding from 'hooks/Bank/useInitiateAccountableBankingOnboarding';
import { worldThemeContext } from 'hooks/shared/useWorldTheme';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BankProviders } from 'types/banks.types';
import type { MonthlyPaymentVolume } from 'types/settings.types';
import { AvailableCountries, type IUser } from 'types/users.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEBAPP_BANKING_ONBOARDING_VISITEDAUTOMATETAXESSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDMEETACCOUNTABLEBAKINGSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEOURPROMISESSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPERSONALDETAILSFORMSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPIDENTITYCONFIRMATIONSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN,
} from 'utils/amplitude/events/bank.amplitude';
import prefetchOnboardingImages from 'utils/prefetchOnboardingImages';
import FinanzamtDocUploader from '../Onboarding/steps/FinanzamtDocUploader.component';
import OnboardingForm from '../Onboarding/steps/OnboardingForm.component';
import AutomaticallyReserveTaxes from '../Onboarding/steps/AutomaticallyReserveTaxes.component';
import ConfirmIdentity from '../Onboarding/steps/ConfirmIdentity.component';
import MeetAccountableBanking from '../Onboarding/steps/MeetAccountableBanking.component';
import FullTransparency from '../Onboarding/steps/FullTransparency.component';

const graphicIconsTransition = {
  scale: 1,
  opacity: 0.85,
  transition: { duration: 0.3 },
};

export const WalletOnboarding: FC<{
  initialScreenIndex?: number;
  onClose?: () => void;
}> = ({ initialScreenIndex, onClose }) => {
  console.log('Old approach WalletOnboarding.component.tsx');
  const { t } = useTranslation();

  const bankingOnboardingQuery = useGetBankingOnboardingStatus();

  const { data: user } = useGetCurrentCustomerData();

  const { mutate, isLoading } = useCreateBankConnection(
    {
      numberOfConnectors: 0,
    },
    user?.country === AvailableCountries.germany,
  );

  const [currentStep, setCurrentStep] = useState(initialScreenIndex || 0);

  const [onboardingId, setOnboardingId] = useState<string>('');

  const [accountDetails, setAccountDetails] = useState<
    IUser & {
      monthlyPaymentVolume: MonthlyPaymentVolume;
      companyName: string;
      additionalBusinessActivityDescription: string;
    }
  >();

  const [proofOfRegistrationFiles, setProofOfRegistrationFiles] = useState<
    {
      url: string;
      payload: string;
    }[]
  >([]);

  const onboardingAPI = useInitiateAccountableBankingOnboarding();

  const steps = [
    {
      event: WEBAPP_BANKING_ONBOARDING_VISITEDMEETACCOUNTABLEBAKINGSCREEN,
      content: (
        <MeetAccountableBanking
          onConfirm={() => setCurrentStep((step) => step + 1)}
        />
      ),
    },

    {
      event: WEBAPP_BANKING_ONBOARDING_VISITEDAUTOMATETAXESSCREEN,
      content: (
        <AutomaticallyReserveTaxes
          onConfirm={() => setCurrentStep((step) => step + 1)}
        />
      ),
    },

    {
      event: WEBAPP_BANKING_ONBOARDING_VISITEOURPROMISESSCREEN,
      content: (
        <FullTransparency
          onConfirm={() => setCurrentStep((step) => step + 1)}
        />
      ),
    },

    {
      event: WEBAPP_BANKING_ONBOARDING_VISITEPERSONALDETAILSFORMSCREEN,
      content: (
        <OnboardingForm
          key="6"
          isConfirming={onboardingAPI.isLoading}
          onConfirm={(values) => {
            setAccountDetails(values);

            if (user?.country === AvailableCountries.belgium) {
              return setCurrentStep((step) => step + 1);
            }

            return onboardingAPI.mutate(
              {
                userId: user?._id,
                country: user?.country,
                taxID: values?.steuernummer,
                personalDetails: {
                  firstName: values?.firstName,
                  lastName: values?.lastName,
                  email: values?.email,
                  address: {
                    city: values?.address?.city,
                    street: values?.address?.street,
                    zip: values?.address?.zip,
                    state: values?.address?.state,
                  },
                },
                businessActivity: values?.APC1,
                businessActivityDescription: t(
                  `${user?.country}.APC2.${values?.APC2}`,
                ),
                monthlyPaymentVolume: values?.monthlyPaymentVolume,
                // Transform null companyName to undefined to prevent null values being sent to backend
                companyName: values?.companyName ?? undefined,
                additionalBusinessActivityDescription:
                  values?.additionalBusinessActivityDescription,
              },
              {
                onSuccess: (data) => {
                  setOnboardingId(data._id);

                  return setCurrentStep((step) => step + 1);
                },
              },
            );
          }}
          currentAccountDetailsValues={accountDetails}
        />
      ),
    },

    user?.country === AvailableCountries.germany
      ? {
          event: WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN,
          content: (
            <FinanzamtDocUploader
              key="7"
              onUploaded={(file) =>
                setProofOfRegistrationFiles((files) => files.concat(file))
              }
              isConfirming={isLoading || onboardingAPI.isLoading}
              onConfirm={() => setCurrentStep((step) => step + 1)}
              onSkip={() => {
                bankingOnboardingQuery.refetch().then(() => {
                  onClose?.();
                });
              }}
              defaultFiles={proofOfRegistrationFiles}
              files={proofOfRegistrationFiles}
              setFiles={setProofOfRegistrationFiles}
              onboardingId={onboardingId}
            />
          ),
        }
      : null,

    {
      event: WEBAPP_BANKING_ONBOARDING_VISITEPIDENTITYCONFIRMATIONSCREEN,
      content: (
        <ConfirmIdentity
          isConfirming={isLoading || onboardingAPI.isLoading}
          onConfirm={() => {
            if (user?.country === AvailableCountries.belgium) {
              return onboardingAPI.mutate(
                {
                  userId: user?._id,
                  country: user?.country,
                  taxID: accountDetails?.VATNumber,
                  personalDetails: {
                    firstName: accountDetails?.firstName,
                    lastName: accountDetails?.lastName,
                    email: accountDetails?.email,
                    address: {
                      city: accountDetails?.address?.city,
                      street: accountDetails?.address?.street,
                      zip: accountDetails?.address?.zip,
                    },
                  },
                  businessActivity: accountDetails?.APC1,
                  businessActivityDescription: t(
                    `${user?.country}.APC2.${accountDetails?.APC2}`,
                  ),
                  monthlyPaymentVolume: accountDetails?.monthlyPaymentVolume,
                  // Transform null companyName to undefined to prevent null values being sent to backend
                  companyName: accountDetails?.companyName ?? undefined,
                  additionalBusinessActivityDescription:
                    accountDetails?.additionalBusinessActivityDescription,
                },
                {
                  onSuccess: () => {
                    mutate({ provider: BankProviders.swan });
                  },
                },
              );
            }

            return mutate(
              { provider: BankProviders.swan },
              {
                onSuccess: (data) => {
                  onboardingAPI.mutate(
                    {
                      _id: onboardingId,
                      userId: user?._id,
                      country: user?.country,
                      taxID: accountDetails?.steuernummer,
                      filepaths: proofOfRegistrationFiles?.map(
                        (file) => file.url,
                      ),
                      personalDetails: {
                        firstName: accountDetails?.firstName,
                        lastName: accountDetails?.lastName,
                        email: accountDetails?.email,
                        address: {
                          city: accountDetails?.address?.city,
                          street: accountDetails?.address?.street,
                          zip: accountDetails?.address?.zip,
                          state: accountDetails?.address?.state,
                        },
                      },
                      businessActivity: accountDetails?.APC1,
                      businessActivityDescription: t(
                        `${user?.country}.APC2.${accountDetails?.APC2}`,
                      ),
                    },
                    {
                      onSuccess: () => {
                        window.location.href = data.redirectUri;
                      },
                    },
                  );
                },
              },
            );
          }}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        <motion.div
          className={currentStep === 0 ? 'opacity-0' : 'opacity-100'}
          initial={{ opacity: 0 }}
          animate={{
            opacity: currentStep === 0 ? 0 : 1,
            transition: { delay: 0.2 },
          }}
          exit={{ opacity: 0 }}
        >
          <Button
            asDefault
            className="flex !items-center space-x-2 group"
            onClick={() => {
              // Check if we're on the FinanzamtDocUploader step before state update
              const currentStepConfig = steps[currentStep];
              const isFinanzamtStep =
                user?.country === AvailableCountries.germany &&
                currentStepConfig?.event ===
                  WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN;
              // If isFinanzamtStep true we should erase the files
              if (isFinanzamtStep) {
                setProofOfRegistrationFiles([]);
              }

              setCurrentStep((step) => step - 1);
            }}
          >
            <ArrowLeftIcon className="h-4 text-primary-500 group-hover:text-primary-700" />

            <span className="text-primary-500 text-sm font-semibold group-hover:text-primary-700">
              {t('back')}
            </span>
          </Button>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <UseEffect
          effect={() => {
            logAmplitude(steps[currentStep]?.event || '');
          }}
          deps={[currentStep]}
          render={() => steps[currentStep]?.content}
        />
      </AnimatePresence>
    </div>
  );
};

const WalletOnboardingModal: FC<{
  Trigger?: FC<{ api: ModalApi }>;
  initialScreenIndex?: number;
  trigger?: 'button' | 'alert';
  name: string;
  defaultOpen?: boolean;
  onClose?: () => void;
  onDismiss?: () => void;
}> = ({
  trigger,
  initialScreenIndex,
  Trigger,
  name,
  defaultOpen,
  onClose,
  onDismiss,
}) => {
  prefetchOnboardingImages();

  const { t } = useTranslation();

  return (
    <Modal
      name={`Accountable Banking onboarding modal - ${name}`}
      className="!max-w-[40rem] WalletOnboardingModal"
      color="bank"
      defaultOpen={defaultOpen}
      onClose={onClose}
      trigger={(api) =>
        defaultOpen && !Trigger ? null : Trigger ? (
          <Trigger api={api} />
        ) : trigger === 'alert' ? (
          <div>
            <SimpleAlertV2
              name="Set money aside banner"
              CustomIcon={WalletPercentIcon}
              onBannerClick={() => api.openModal()}
              title={t(
                'accountable_banking.onboarding.default_trigger_2.alert.title',
              )}
              subtitle={t(
                'accountable_banking.onboarding.default_trigger_2.alert.subtitle',
              )}
              Graphics={(props) => {
                const iconsAnimation = props.isHovered
                  ? graphicIconsTransition
                  : {};

                return (
                  <div className="flex-1 pl-28">
                    <motion.div className="relative flex items-center justify-center max-w-[190px]">
                      <motion.div
                        className="absolute -top-1 -right-8 "
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={iconsAnimation}
                      >
                        <FilePercentIcon className="w-7 text-taxes" />
                      </motion.div>

                      <motion.div
                        className="absolute -top-1 -left-8"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={iconsAnimation}
                      >
                        <GivingCoinsIcon className="w-6 text-taxes -rotate-[25deg]" />
                      </motion.div>

                      <motion.div
                        className="absolute -bottom-[4.5rem] -left-11"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={iconsAnimation}
                      >
                        <PegIconOutline className="w-8 text-taxes" />
                      </motion.div>

                      <motion.div
                        className="absolute -bottom-[4.5rem] -right-12"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={iconsAnimation}
                      >
                        <EuroCircleIcon className="w-8 text-taxes" />
                      </motion.div>

                      <motion.img
                        initial={{
                          translateY: 65,
                        }}
                        animate={{
                          translateY: props.isHovered ? 45 : 65,
                          transition: { ease: 'easeInOut' },
                        }}
                        src={walletBannerGraphics}
                        className="absolute"
                      />
                    </motion.div>
                  </div>
                );
              }}
              type="info"
              dismissable
              onDismiss={() => onDismiss?.()}
              wrapperClassName="mt-5 shadow-sm"
              verticalAlignment="top"
              extraContent={
                <Button
                  structure="text"
                  className="underline mt-1"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();

                    api.openModal();
                  }}
                >
                  {t('learn_more')}
                </Button>
              }
            />
          </div>
        ) : (
          <GraphicButton
            closable
            onClose={onDismiss ? () => onDismiss?.() : undefined}
            label={t('accountable_banking.setup_account_cta')}
            description={t(
              'accountable_banking.onboarding.default_trigger.button.subtitle',
            )}
            className="border-taxes-300 bg-taxes-50 hover:bg-taxes-50"
            // icon={PlusIcon}
            color="taxes"
            buttonGraphicSrc={accountableWalletGraphic}
            onClick={() => api.openModal()}
          />
        )
      }
      render={(api) => (
        <worldThemeContext.Provider value="bank">
          <UnifiedBankingSignupFlow
            initialScreenIndex={initialScreenIndex}
            onComplete={api.closeModal}
          />
        </worldThemeContext.Provider>
      )}
    />
  );
};

export default WalletOnboardingModal;
